// ═══════════════════════════════════════════════════════════════════════════
// SIMULATEUR CULTURAL - Mode Démo avec Timeline Interactive
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/SimulateurCultural.jsx
// Farm-Booking - Papy 73+
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Sprout, Leaf, AlertTriangle, CheckCircle, Clock,
  ArrowRight, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Info
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO
// ═══════════════════════════════════════════════════════════════════════════

const PARCELLES_DEMO = [
  { id: 'A', nom: 'Parcelle A', surface: 50 },
  { id: 'B', nom: 'Parcelle B', surface: 50 },
  { id: 'C', nom: 'Parcelle C', surface: 50 },
  { id: 'D', nom: 'Parcelle D', surface: 50 },
  { id: 'E', nom: 'Parcelle E', surface: 50 },
];

const CULTURES_DEMO = {
  A: {
    nom: 'Chou cabus', icone: '🥬', famille: 'Brassicacées',
    phases: {
      semis: { debut: '02-15', fin: '03-15', lieu: 'Serre pépinière' },
      repiquage: { debut: '04-15', fin: '05-01' },
      croissance: { debut: '05-01', fin: '09-01' },
      recolte: { debut: '09-15', fin: '12-31' }
    },
    rendement: '3-5 kg/m²',
    conseils: {
      '02': 'Semis en pépinière à 15-18°C. Terreau léger.',
      '03': 'Éclaircir si nécessaire. Endurcir avant repiquage.',
      '04': 'Repiquage après les dernières gelées. Espacement 50x60cm.',
      '05': 'Arrosage régulier. Surveiller les altises.',
      '06': 'Binage, paillage. Attention limaces !',
      '07': 'Arrosage si sécheresse. Les pommes se forment.',
      '08': 'Surveiller piéride du chou. Traitement bio si besoin.',
      '09': 'Début récolte choux précoces.',
      '10': 'Récolte principale. Stocker au frais.',
      '11': 'Récolte continue. Supporter légères gelées.',
      '12': 'Fin de récolte. Arracher les trognons.'
    }
  },
  B: {
    nom: 'Courge Butternut', icone: '🎃', famille: 'Cucurbitacées',
    phases: {
      semis: { debut: '04-15', fin: '05-15', lieu: 'Godets sous abri' },
      repiquage: { debut: '05-20', fin: '06-15' },
      croissance: { debut: '06-15', fin: '09-01' },
      recolte: { debut: '09-15', fin: '11-15' }
    },
    rendement: '2-4 kg/m²',
    conseils: {
      '04': 'Semis en godets, 2 graines par godet. 20-25°C.',
      '05': 'Plantation après Saints de Glace. Sol réchauffé.',
      '06': 'Paillage épais. Arrosage au pied.',
      '07': 'Floraison. Pollinisation par insectes.',
      '08': 'Fruits grossissent. Limiter à 4-5 fruits/pied.',
      '09': 'Maturation. Pédoncule liégeux = mûr.',
      '10': 'Récolte avant gelées. Laisser ressuyer.',
      '11': 'Fin récolte. Conservation 6 mois en cave.'
    }
  },
  C: {
    nom: 'Poireau Solaise', icone: '🧅', famille: 'Alliacées',
    phases: {
      semis: { debut: '02-15', fin: '03-31', lieu: 'Pleine terre / abri' },
      repiquage: { debut: '06-01', fin: '07-31' },
      croissance: { debut: '07-01', fin: '10-01' },
      recolte: { debut: '10-15', fin: '04-30' }
    },
    rendement: '2.5-4 kg/m²',
    conseils: {
      '02': 'Semis en pépinière. Terre fine, humide.',
      '03': 'Levée lente. Garder humide.',
      '04': 'Plants grandissent. Éclaircir.',
      '05': 'Préparer planches. Attendre plants crayon.',
      '06': 'Repiquage ! Habiller plants. Planter profond.',
      '07': 'Arrosage, binage. Premier buttage.',
      '08': 'Buttages successifs pour blanchir fût.',
      '09': 'Surveiller teigne. Buttage final.',
      '10': 'Début récolte au fur et à mesure.',
      '11': 'Récolte continue. Pailler si grand froid.',
      '12': 'Récolte hiver. Résiste au gel.',
      '01': 'Récolte possible même sous neige !'
    }
  },
  D: {
    nom: 'Carotte Nantaise', icone: '🥕', famille: 'Apiacées',
    phases: {
      semis: { debut: '03-15', fin: '07-15', lieu: 'Pleine terre' },
      croissance: { debut: '04-01', fin: '09-01' },
      recolte: { debut: '07-01', fin: '11-30' }
    },
    rendement: '3-6 kg/m²',
    conseils: {
      '03': 'Semis en place. Sol meuble, sans cailloux !',
      '04': 'Levée lente (15-20j). Patience !',
      '05': 'Éclaircir à 5cm. Butter légèrement.',
      '06': 'Binage, paillage. Associer avec poireaux.',
      '07': 'Début récolte primeurs.',
      '08': 'Récolte continue. Arrosage si sec.',
      '09': 'Récolte principale.',
      '10': 'Stocker en silo.',
      '11': 'Récolte avant grosses gelées.'
    }
  },
  E: {
    nom: 'JACHÈRE', icone: '🌿', famille: 'Repos du sol',
    phases: {
      semis: { debut: '09-01', fin: '10-15', lieu: 'Engrais vert' },
      croissance: { debut: '10-01', fin: '03-01' },
      destruction: { debut: '03-01', fin: '04-01' }
    },
    rendement: 'Régénération sol',
    conseils: {
      '01': 'Engrais vert en place.',
      '02': 'Prévoir destruction.',
      '03': 'Broyage/enfouissement.',
      '04': 'Sol se régénère.',
      '05': 'Sol au repos.',
      '06': 'Sol au repos.',
      '07': 'Analyse de sol possible.',
      '08': 'Préparer semis engrais vert.',
      '09': 'Semis engrais vert (moutarde, phacélie).',
      '10': 'Levée engrais vert.',
      '11': 'Protection hivernale.',
      '12': 'Couverture du sol.'
    }
  }
};

const MOIS = [
  { id: 1, nom: 'Janvier', court: 'Jan', icone: '❄️' },
  { id: 2, nom: 'Février', court: 'Fév', icone: '❄️' },
  { id: 3, nom: 'Mars', court: 'Mar', icone: '🌱' },
  { id: 4, nom: 'Avril', court: 'Avr', icone: '🌸' },
  { id: 5, nom: 'Mai', court: 'Mai', icone: '☀️' },
  { id: 6, nom: 'Juin', court: 'Jun', icone: '☀️' },
  { id: 7, nom: 'Juillet', court: 'Jul', icone: '🌡️' },
  { id: 8, nom: 'Août', court: 'Aoû', icone: '🌡️' },
  { id: 9, nom: 'Septembre', court: 'Sep', icone: '🍂' },
  { id: 10, nom: 'Octobre', court: 'Oct', icone: '🍂' },
  { id: 11, nom: 'Novembre', court: 'Nov', icone: '🌧️' },
  { id: 12, nom: 'Décembre', court: 'Déc', icone: '❄️' }
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const parseDate = (mmdd) => {
  if (!mmdd) return null;
  const [mm, dd] = mmdd.split('-').map(Number);
  return { month: mm, day: dd };
};

const isInRange = (currentMonth, debut, fin) => {
  if (!debut) return false;
  const d = parseDate(debut);
  const f = fin ? parseDate(fin) : { month: 12, day: 31 };
  if (!d) return false;
  if (d.month <= f.month) {
    return currentMonth >= d.month && currentMonth <= f.month;
  } else {
    return currentMonth >= d.month || currentMonth <= f.month;
  }
};

const getPhaseActive = (culture, mois) => {
  for (const [phase, dates] of Object.entries(culture.phases)) {
    if (isInRange(mois, dates.debut, dates.fin)) return phase;
  }
  return 'repos';
};

const getPhaseLabel = (phase) => {
  const labels = {
    semis: '🌱 Semis', repiquage: '🌿 Repiquage', croissance: '📈 Croissance',
    recolte: '🧺 Récolte', destruction: '🔄 Destruction', repos: '💤 Repos'
  };
  return labels[phase] || phase;
};

const getPhaseColor = (phase) => {
  const colors = {
    semis: 'bg-emerald-100 text-emerald-800',
    repiquage: 'bg-green-100 text-green-800',
    croissance: 'bg-blue-100 text-blue-800',
    recolte: 'bg-amber-100 text-amber-800',
    destruction: 'bg-gray-100 text-gray-800',
    repos: 'bg-gray-50 text-gray-500'
  };
  return colors[phase] || colors.repos;
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const SimulateurCultural = () => {
  const [moisActuel, setMoisActuel] = useState(new Date().getMonth() + 1);
  const [annee] = useState(2025);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedParcelle, setSelectedParcelle] = useState(null);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setMoisActuel(m => m >= 12 ? (setIsPlaying(false), 12) : m + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isPlaying]);

  const etatParcelles = useMemo(() => {
    return PARCELLES_DEMO.map(parcelle => {
      const culture = CULTURES_DEMO[parcelle.id];
      const phase = getPhaseActive(culture, moisActuel);
      const moisStr = String(moisActuel).padStart(2, '0');
      const conseil = culture.conseils[moisStr] || 'Pas d\'action ce mois.';
      return { ...parcelle, culture, phase, conseil };
    });
  }, [moisActuel]);

  const imperatifs = useMemo(() => {
    const actions = [];
    etatParcelles.forEach(p => {
      if (p.phase === 'semis') {
        actions.push({ type: 'action', parcelle: p.nom, message: `Semis ${p.culture.nom}`, icone: '🌱' });
      }
      if (p.phase === 'repiquage') {
        actions.push({ type: 'action', parcelle: p.nom, message: `Repiquage ${p.culture.nom}`, icone: '🌿' });
      }
      if (p.phase === 'recolte') {
        actions.push({ type: 'recolte', parcelle: p.nom, message: `Récolte ${p.culture.nom}`, icone: '🧺' });
      }
    });
    if (moisActuel >= 3 && moisActuel <= 4) {
      actions.push({ type: 'meteo', message: '⚠️ Risque gelées tardives', icone: '❄️' });
    }
    if (moisActuel >= 7 && moisActuel <= 8) {
      actions.push({ type: 'meteo', message: '💧 Arrosage régulier requis', icone: '🌡️' });
    }
    return actions;
  }, [moisActuel, etatParcelles]);

  const moisInfo = MOIS[moisActuel - 1];

  return (
    <div className="space-y-6 p-4 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white bg-opacity-20 p-3 rounded-xl">
              <Sprout className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">🌱 Simulateur Cultural</h1>
              <p className="text-green-100">Cycle des cultures sur l'année</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-lg">
            <Calendar className="h-5 w-5" />
            <span className="text-lg font-semibold">{moisInfo.icone} {moisInfo.nom} {annee}</span>
          </div>
        </div>
      </div>

      {/* TIMELINE SLIDER */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-green-600" />
            Timeline {annee}
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setMoisActuel(1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <RotateCcw className="h-5 w-5 text-gray-600" />
            </button>
            <button onClick={() => setMoisActuel(m => Math.max(1, m - 1))} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`p-2 rounded-lg ${isPlaying ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <button onClick={() => setMoisActuel(m => Math.min(12, m + 1))} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex justify-between mb-2">
          {MOIS.map(m => (
            <button
              key={m.id}
              onClick={() => setMoisActuel(m.id)}
              className={`flex-1 text-center py-2 text-xs font-medium ${m.id === moisActuel ? 'text-green-700 font-bold' : 'text-gray-500'}`}
            >
              {m.court}
            </button>
          ))}
        </div>

        <input
          type="range" min="1" max="12" value={moisActuel}
          onChange={(e) => setMoisActuel(parseInt(e.target.value))}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{ background: 'linear-gradient(to right, #bfdbfe 0%, #bbf7d0 25%, #fef08a 50%, #fed7aa 75%, #bfdbfe 100%)' }}
        />

        <div className="flex justify-center gap-4 mt-6 text-sm">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-200 rounded"></span> Hiver</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-200 rounded"></span> Printemps</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-200 rounded"></span> Été</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-200 rounded"></span> Automne</span>
        </div>
      </div>

      {/* IMPÉRATIFS */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Impératifs de {moisInfo.nom}
        </h2>
        {imperatifs.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
            Pas d'action urgente
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {imperatifs.map((imp, idx) => (
              <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                imp.type === 'recolte' ? 'bg-amber-50 border-amber-500' :
                imp.type === 'meteo' ? 'bg-blue-50 border-blue-500' :
                'bg-green-50 border-green-500'
              }`}>
                <div className="flex items-start gap-2">
                  <span className="text-xl">{imp.icone}</span>
                  <div>
                    <div className="font-medium text-gray-800">{imp.message}</div>
                    {imp.parcelle && <div className="text-sm text-gray-500">{imp.parcelle}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PARCELLES */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Leaf className="h-5 w-5 text-green-600" />
          État des parcelles
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {etatParcelles.map(parcelle => (
            <div
              key={parcelle.id}
              onClick={() => setSelectedParcelle(selectedParcelle === parcelle.id ? null : parcelle.id)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                selectedParcelle === parcelle.id ? 'border-green-500 shadow-lg' : 'border-gray-200'
              }`}
            >
              <div className="text-4xl text-center mb-2">{parcelle.culture.icone}</div>
              <div className="text-center font-semibold text-gray-800">{parcelle.nom}</div>
              <div className="text-center text-sm text-gray-600 mb-2">{parcelle.culture.nom}</div>
              <div className={`text-center text-xs px-2 py-1 rounded-full ${getPhaseColor(parcelle.phase)}`}>
                {getPhaseLabel(parcelle.phase)}
              </div>
              <div className="text-center text-xs text-gray-400 mt-2">{parcelle.surface} m²</div>
            </div>
          ))}
        </div>
      </div>

      {/* DÉTAIL PARCELLE */}
      {selectedParcelle && (() => {
        const p = etatParcelles.find(x => x.id === selectedParcelle);
        if (!p) return null;
        return (
          <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-2xl">{p.culture.icone}</span>
                {p.nom} - {p.culture.nom}
              </h2>
              <button onClick={() => setSelectedParcelle(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 text-sm">
                <div><span className="font-medium text-gray-600">Famille :</span> {p.culture.famille}</div>
                <div><span className="font-medium text-gray-600">Rendement :</span> {p.culture.rendement}</div>
                <div><span className="font-medium text-gray-600">Phase :</span> <span className={`px-2 py-0.5 rounded-full text-xs ${getPhaseColor(p.phase)}`}>{getPhaseLabel(p.phase)}</span></div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-green-800 mb-1">Conseil {moisInfo.nom}</div>
                    <div className="text-sm text-green-700">{p.conseil}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <h3 className="font-medium text-gray-700 mb-2">Phases :</h3>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(p.culture.phases).map(([phase, dates], idx, arr) => (
                  <React.Fragment key={phase}>
                    <div className={`px-3 py-1 rounded-lg text-sm ${p.phase === phase ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {getPhaseLabel(phase)}
                      <div className="text-xs opacity-75">{dates.debut} → {dates.fin || '...'}</div>
                    </div>
                    {idx < arr.length - 1 && <ArrowRight className="h-4 w-4 text-gray-400" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* GANTT */}
      <div className="bg-white rounded-xl p-6 shadow-lg overflow-x-auto">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          Vue annuelle
        </h2>
        <div className="min-w-[700px]">
          <div className="flex border-b border-gray-200 mb-2">
            <div className="w-28 flex-shrink-0 font-medium text-gray-600 text-sm p-2">Parcelle</div>
            {MOIS.map(m => (
              <div key={m.id} className={`flex-1 text-center text-xs p-2 ${m.id === moisActuel ? 'bg-green-100 font-bold' : 'text-gray-500'}`}>
                {m.court}
              </div>
            ))}
          </div>
          {PARCELLES_DEMO.map(parcelle => {
            const culture = CULTURES_DEMO[parcelle.id];
            return (
              <div key={parcelle.id} className="flex items-center border-b border-gray-100">
                <div className="w-28 flex-shrink-0 p-2 flex items-center gap-2">
                  <span>{culture.icone}</span>
                  <span className="text-sm font-medium">{parcelle.id}</span>
                </div>
                {MOIS.map(m => {
                  const phase = getPhaseActive(culture, m.id);
                  return (
                    <div key={m.id} className={`flex-1 h-8 ${m.id === moisActuel ? 'ring-2 ring-green-500 ring-inset' : ''}`}>
                      {phase !== 'repos' && (
                        <div className={`w-full h-6 rounded ${
                          phase === 'semis' ? 'bg-emerald-400' :
                          phase === 'repiquage' ? 'bg-green-400' :
                          phase === 'croissance' ? 'bg-blue-400' :
                          phase === 'recolte' ? 'bg-amber-400' : 'bg-gray-300'
                        }`} title={`${culture.nom} - ${phase}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div className="flex justify-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-emerald-400 rounded"></span> Semis</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-green-400 rounded"></span> Repiquage</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-blue-400 rounded"></span> Croissance</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-amber-400 rounded"></span> Récolte</span>
          </div>
        </div>
      </div>

      {/* ROTATION 5 ANS */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 shadow-lg border border-purple-200">
        <h2 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          Plan de rotation 5 ans
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-purple-600">
                <th className="text-left p-2">Parcelle</th>
                <th className="text-center p-2">2024</th>
                <th className="text-center p-2 bg-purple-100 rounded">2025</th>
                <th className="text-center p-2">2026</th>
                <th className="text-center p-2">2027</th>
                <th className="text-center p-2">2028</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['A', '🎃 Courges', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère'],
                ['B', '🌿 Jachère', '🎃 Courges', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes'],
                ['C', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère', '🎃 Courges'],
                ['D', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère', '🎃 Courges', '🥬 Choux'],
                ['E', '🥕 Carottes', '🌿 Jachère', '🎃 Courges', '🥬 Choux', '🧅 Poireaux'],
              ].map(row => (
                <tr key={row[0]} className="border-t border-purple-200">
                  <td className="p-2 font-medium">Parcelle {row[0]}</td>
                  <td className="p-2 text-center">{row[1]}</td>
                  <td className="p-2 text-center bg-purple-100 font-medium">{row[2]}</td>
                  <td className="p-2 text-center">{row[3]}</td>
                  <td className="p-2 text-center">{row[4]}</td>
                  <td className="p-2 text-center">{row[5]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 p-3 bg-white rounded-lg text-sm text-purple-700">
          <strong>💡 Principe :</strong> Chaque culture tourne d'une parcelle chaque année. La jachère régénère le sol.
        </div>
      </div>

      <div className="text-center text-sm text-gray-500 py-4">
        🌱 Simulateur Cultural - Farm-Booking - Papy 73+ 📡
      </div>
    </div>
  );
};

export default SimulateurCultural;
